import { fabric } from "fabric";
import Observer from "../../frontend/services/helpers/Observer";
import { parseJsonData } from "../../frontend/services/helpers";
import { parseStyleToText } from "./parse-style-to-text";
import { COPY_PASTE_EVENTS, copyPasteEventBroker } from "@frontend/group/modules/copy-paste-object/event-broker";
import {setSelectedInstance} from "@frontend/group/modules/copy-paste-object";
import {getScaledAdditionalLineGeometry} from "@/common/preview/additional-line-geometry";

const clipboard = window.navigator.clipboard;
const observer = new Observer();
let scope = {};

const hasNumericValue = (value) => (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    Number.isFinite(Number(value))
);

export function serializeCanvasSelection(canvas) {
    if (!(canvas instanceof fabric.Canvas)) return;

    const activeObject = canvas.getActiveObject();

    // Check if object is selectable (works for lines too, which don't have hasControls)
    if (!activeObject || (!activeObject?.hasControls && !activeObject?.selectable)) return;

    // Check if multiple objects are selected (ActiveSelection)
    const isMultipleSelection = activeObject.type === 'activeSelection';
    let objectsToCopy = isMultipleSelection ? activeObject.getObjects() : [activeObject];

    // Track which objects are from the original selection (have relative coords)
    // vs added later (have absolute coords)
    const objectsFromSelection = new Set(objectsToCopy);

    // If multiple selection, also include non-selectable lines that are within the selection bounds
    if (isMultipleSelection) {
        const selectionBounds = activeObject.getBoundingRect();
        const allCanvasObjects = canvas.getObjects();

        // Find lines that are within the selection bounds but NOT in the active selection
        const additionalLines = allCanvasObjects.filter(obj => {
            if (obj.type !== 'line') return false;

            // Skip system/positioning lines (they have specific IDs)
            if (obj.id && (
                obj.id.includes('template-center-line') ||
                obj.id.includes('align-') ||
                obj.id.includes('intersect')
            )) {
                return false;
            }

            if (objectsToCopy.includes(obj)) {
                return false;
            }

            // Check if line overlaps with or is within selection bounds (with tolerance)
            const objBounds = obj.getBoundingRect();
            const tolerance = 100;
            const isWithinBounds =
                objBounds.left < selectionBounds.left + selectionBounds.width + tolerance &&
                objBounds.left + objBounds.width > selectionBounds.left - tolerance &&
                objBounds.top < selectionBounds.top + selectionBounds.height + tolerance &&
                objBounds.top + objBounds.height > selectionBounds.top - tolerance;

            return isWithinBounds;
        });

        if (additionalLines.length > 0) {
            objectsToCopy = [...objectsToCopy, ...additionalLines];
        }

        // Find tables that are within the selection bounds but NOT in the active selection
        const additionalTables = allCanvasObjects.filter(obj => {
            // Check if it's a table by id or fieldType
            const isTable = (obj.id && obj.id.startsWith('add_html_table_')) ||
                           obj.fieldType === 'Additional HTML Table';

            if (!isTable) return false;

            if (objectsToCopy.includes(obj)) {
                return false;
            }

            // Check if table overlaps with or is within selection bounds
            const objBounds = obj.getBoundingRect();
            const tolerance = 50;
            const isWithinBounds =
                objBounds.left < selectionBounds.left + selectionBounds.width + tolerance &&
                objBounds.left + objBounds.width > selectionBounds.left - tolerance &&
                objBounds.top < selectionBounds.top + selectionBounds.height + tolerance &&
                objBounds.top + objBounds.height > selectionBounds.top - tolerance;

            return isWithinBounds;
        });

        if (additionalTables.length > 0) {
            objectsToCopy = [...objectsToCopy, ...additionalTables];
        }
    }

    // Helper function to serialize a single object
    const serializeObject = (obj, index) => {
        let objectData = obj.toObject();

        // Calculate absolute coordinates using getBoundingRect for accuracy
        let absoluteLeft, absoluteTop;

        if (isMultipleSelection && activeObject && objectsFromSelection.has(obj)) {
            // For objects in ActiveSelection, we need to transform their coordinates
            // Use the object's aCoords (absolute coordinates) after applying group transform
            const groupMatrix = activeObject.calcTransformMatrix();
            const point = fabric.util.transformPoint(
                { x: obj.left, y: obj.top },
                groupMatrix
            );
            absoluteLeft = point.x;
            absoluteTop = point.y;
        } else {
            // For auto-detected objects or single selection, use their direct coordinates
            absoluteLeft = obj.left;
            absoluteTop = obj.top;
        }

        // Helper to extract plain object from Vue reactive objects
        const extractPlainObject = (data) => {
            if (!data) return null;
            if (typeof data !== 'object') return data;
            // Convert to plain object using JSON parse/stringify to remove Vue reactivity
            try {
                return JSON.parse(JSON.stringify(data));
            } catch (e) {
                return data;
            }
        };

        const shadowData = obj?.shadow ? (obj.shadow.toObject ? obj.shadow.toObject() : obj.shadow) : null;
        const outlineShadowData = obj?.outlineShadow ? extractPlainObject(obj.outlineShadow) : null;
        const mirrorShadowData = obj?.mirrorShadow ? extractPlainObject(obj.mirrorShadow) : null;
        const ovalShadowData = obj?.ovalShadow ? extractPlainObject(obj.ovalShadow) : null;

        objectData = {
            ...objectData,
            name: obj?.templateField?.name || obj?.name || null,
            fieldType: obj?.templateField?.type || obj?.fieldType || null,
            fontColor: obj?.fontColor || '#000000',
            fontSize: (obj?.fontSize * obj?.scaleX) || 100,
            fileName: obj?.fileName || null,
            shadow: shadowData,
            outlineShadow: outlineShadowData,
            mirrorShadow: mirrorShadowData,
            ovalShadow: ovalShadowData,
            id: obj.id,
            order: obj.order,
            prevInstanceId: canvas.instance_id,
            realScaleX: obj?.scaleX,
            realScaleY: obj?.scaleY,
            sourceTemplateWidth: canvas.width,
            sourceTemplateHeight: canvas.height,
            originalWidth: obj?.originalWidth || null,
            originalHeight: obj?.originalHeight || null,
            originalScaleX: obj?.originalScaleX || null,
            originalScaleY: obj?.originalScaleY || null,
            // Store absolute coordinates
            absoluteLeft: absoluteLeft,
            absoluteTop: absoluteTop,
        }

        const hasOriginalDimensions = [objectData.originalWidth, objectData.originalHeight]
            .every(hasNumericValue);
        if (
            hasOriginalDimensions &&
            objectData.width &&
            objectData.height &&
            +objectData.width !== +objectData.originalWidth &&
            +objectData.height !== +objectData.originalHeight
        ) {
            objectData.width = objectData.originalWidth;
            objectData.height = objectData.originalHeight;
            // Only override scale values if originalScale values exist
            if (Number.isFinite(Number(objectData.originalScaleX))) {
                objectData.scaleX = objectData.originalScaleX;
                objectData.realScaleX = objectData.originalScaleX;
            }
            if (Number.isFinite(Number(objectData.originalScaleY))) {
                objectData.scaleY = objectData.originalScaleY;
                objectData.realScaleY = objectData.originalScaleY;
            }
            objectData.isOriginalScale = true;
        }

        if (obj.name) {
            objectData.fieldName = obj.name;
        }

        // Calculate the visual size using getScaledWidth/Height which returns the actual rendered size
        // This is the size we want to preserve when pasting
        const hasScaleFunction = typeof obj.getScaledWidth === 'function';
        if (hasScaleFunction) {
            objectData.scaledWidth = obj.getScaledWidth();
            objectData.scaledHeight = obj.getScaledHeight();
        } else {
            objectData.scaledWidth = (obj?.width || 0) * (obj?.scaleX || 1);
            objectData.scaledHeight = (obj?.height || 0) * (obj?.scaleY || 1);
        }

        if (obj.type === 'textbox') {
            objectData.text = parseStyleToText(obj?.textLines, obj?.styles, true);
            objectData.width = obj?.width; //save width of active object (text) cuz there's width is null on toObject method
        }

        if (obj.radius !== undefined) {
            objectData.radius = obj.radius;
        }

        // For shapes (rectangle, circle), preserve fill type and gradient
        if (obj.type === 'rect' || obj.type === 'circle' || obj.type === 'customRect' || obj.type === 'CustomRect') {
            objectData.fillType = obj.fillType || 'solid';
            objectData.gradient = obj.gradient || null;
        }

        // For HTML tables, preserve the table data
        if (obj.type === 'image' && obj.fieldType === 'Additional HTML Table') {
            objectData.tableData = obj.tableData;
            objectData.html_table_data = obj.html_table_data;
            objectData.field_id = obj.field_id;
        }

        // For Product Images, get the original x_offset and y_offset from preview data
        if (obj.fieldType === 'Product Image') {
            const preview = window.previews?.find(p => p.canvas === canvas);
            if (preview?.data) {
                // Extract index from obj.id (e.g., "image_0" -> 0)
                const idParts = obj.id?.split('_');
                const imageIndex = idParts ? parseInt(idParts[idParts.length - 1], 10) : 0;

                objectData.productImageXOffset = preview.data.x_offset?.[imageIndex] || 0;
                objectData.productImageYOffset = preview.data.y_offset?.[imageIndex] || 0;
                objectData.productImageScale = preview.data.scale?.[imageIndex] || 1;
            }
        }

        // For lines, store coords array separately as it needs special handling
        if (obj.type === 'line') {
            const geometry = getScaledAdditionalLineGeometry(obj);

            objectData.coords = geometry.coords;
            objectData.lineLength = geometry.length;
            objectData.lineThickness = geometry.thickness;
            objectData.lineAngle = geometry.angle;
            // For lines, also store x1, y1, x2, y2 from Fabric.js
            objectData.x1 = obj.x1;
            objectData.y1 = obj.y1;
            objectData.x2 = obj.x2;
            objectData.y2 = obj.y2;
            // Store the angle for lines
            objectData.angle = geometry.angle;

            // Use original offset coordinates if available (before dimension transformation)
            // Otherwise fall back to current position
            if (obj._originalOffsetX !== undefined && obj._originalOffsetY !== undefined) {
                absoluteLeft = obj._originalOffsetX;
                absoluteTop = obj._originalOffsetY;
                objectData.absoluteLeft = absoluteLeft;
                objectData.absoluteTop = absoluteTop;
            }
        }

        return objectData;
    };

    // Serialize all objects
    const serializedObjects = objectsToCopy.map(serializeObject);

    return {
        isMultiple: isMultipleSelection,
        objects: serializedObjects
    };
}

export async function copy(canvas) {
    const dataToCopy = serializeCanvasSelection(canvas);

    if (!dataToCopy) return;

    let textToCopy;
    try {
        textToCopy = JSON.stringify(dataToCopy);
    } catch (serializeErr) {
        console.error('Copy failed: could not serialize object data', serializeErr);
        return;
    }

    // Try modern Clipboard API first, then fall back to execCommand
    try {
        await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
        // Fallback using execCommand (works in more contexts)
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
        } catch (execErr) {
            // Last resort: show prompt
            window.prompt("Copy to clipboard: Ctrl+C, Enter", textToCopy);
        }
        document.body.removeChild(textArea);
    }
}

async function paste(nativeEvent = null, options = {}) {
    const { instanceId = null } = options;
    const files = Array.from(nativeEvent?.clipboardData?.files || []);

    let data = null;
    if (navigator.clipboard && navigator.clipboard.writeText) {
        data = await navigator.clipboard.readText();
    } else if (nativeEvent?.clipboardData) {
        data = nativeEvent.clipboardData.getData("text/plain");
    }

    const parseData = parseJsonData(data, null);
    const fileDataUrls = await Promise.all(files.map(file => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }));

    // Check if parseData is the new format with multiple objects
    let isMultiple = false;
    let objectsToPaste = null;

    if (parseData && parseData.isMultiple && Array.isArray(parseData.objects)) {
        isMultiple = true;
        objectsToPaste = parseData.objects;
    } else if (parseData && !parseData.isMultiple && Array.isArray(parseData.objects)) {
        // New format but single object
        objectsToPaste = parseData.objects;
    }

    let eventData = {
        text: parseData ? parseData.text : data,
        object: objectsToPaste ? objectsToPaste[0] : parseData, // For backward compatibility, pass first object as 'object'
        objects: objectsToPaste, // New: array of objects
        isMultiple: isMultiple,
        dataFromClipboard: data,
        files: files,
        dataUrls: fileDataUrls,
        instance: instanceId ? window.previews.find(preview => preview.id === instanceId) : null
    };

    observer.dispatch('paste', eventData);
}
let pasteListenersInitialized = false;
function init() {
  if (pasteListenersInitialized) return;
  pasteListenersInitialized = true;
    document.addEventListener('copy', async function (e) {
        switch (e.target?.tagName) {
            case 'INPUT':
            case 'TEXTAREA':
            case 'P':
                return
        }
        copy(scope.canvas);
    });

    document.addEventListener('paste', async function (e) {
      const target = e.target;
      const previewWithActiveObject = window.previews.find(preview => preview.canvas.getActiveObject());
      if (previewWithActiveObject) {
        const activeObj = previewWithActiveObject.canvas.getActiveObject();

        const isTextbox = activeObj && activeObj.type === 'textbox';

        const isEditingMode = isTextbox && activeObj.isEditing;

        const clipboardText = e.clipboardData.getData('text/plain');

        let isJson = false;
        try {
          const json = JSON.parse(clipboardText);
          if (json && typeof json === 'object') {
            isJson = true;
          }
        } catch (_) {
          // do nothing
        }
        if (isEditingMode && isJson) {
          e.preventDefault();
          return;
        }
      }

      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      paste(e);
    });

  copyPasteEventBroker.on(COPY_PASTE_EVENTS.COPY, ({ canvas }) => copy(canvas));
  copyPasteEventBroker.on(COPY_PASTE_EVENTS.PASTE, ({ canvas }) => {
    setSelectedInstance(canvas.instance_id);
    
    const event = new ClipboardEvent('paste', {clipboardData: new DataTransfer()})
    return document.dispatchEvent(event)
  });
}

function setScope(data) {
    scope = { ...scope, ...data };
}

export default {
    init,
    setScope,
    on: observer.on.bind(observer)
}
